import {
  CommandCenterAppConfigFragment,
  DriveInsightsConfig,
  Drive_Insights_Variant,
  Location_Metrics_Mobile_Pay_Filter,
} from '@occc/graphql';
import { RuntimeConfig } from './runtime-config.types';
import { MIN_SUB_BRANDS_FOR_SELECTION } from './build-runtime.config.constants';
import { DriveInsights } from '../remote/remote-config.types';
import { EnvConfig } from '../index';

type BuildRuntimeConfigParams = {
  remoteConfig: CommandCenterAppConfigFragment;
  envConfig: EnvConfig;
};

const buildDriveInsightsConfig = (
  config: DriveInsightsConfig | null | undefined,
): DriveInsights['config'] => {
  switch (config?.__typename) {
    case 'DriveInsightsConfigV2':
      return { variant: Drive_Insights_Variant.V2, url: config.url };
    case 'DriveInsightsConfigV1':
    default:
      // Default to V1 (the legacy Drive Insights experience) when the config is
      // absent or unrecognized, preserving the previous enabled-only behavior
      // while configs are rolled out.
      return { variant: Drive_Insights_Variant.V1 };
  }
};

export const buildRuntimeConfig = ({
  remoteConfig,
  envConfig,
}: BuildRuntimeConfigParams): RuntimeConfig => {
  return {
    ...envConfig,
    csrLogin: {
      enabled: remoteConfig.csrLogin.enabled,
      authMethod: remoteConfig.csrLogin.authMethod,
    },
    scanData: {
      enabled: remoteConfig.scanData?.enabled ?? false,
    },
    touchpoint: {
      enabled: remoteConfig.touchpoint?.enabled ?? false,
    },
    mobileTransactionFraudCheck: remoteConfig.mobileTransactionFraudCheck
      ? {
          enabled: remoteConfig.mobileTransactionFraudCheck.enabled,
          provider: remoteConfig.mobileTransactionFraudCheck.provider,
        }
      : undefined,
    features: {
      ...envConfig.features,
      segments: {
        version:
          remoteConfig.memberDossier?.enabled && remoteConfig.memberDossier?.useForSegments
            ? '2'
            : '1',
      },
    },
    audienceReportExport: {
      enabled: remoteConfig.audienceReportExport.enabled,
    },
    supportedPaymentTypes: remoteConfig.supportedPaymentTypes || [],
    orderAheadConfig:
      remoteConfig.orderAheadConfig && remoteConfig.orderAheadConfig.enabled
        ? {
            enabled: true,
            provider: remoteConfig.orderAheadConfig.provider,
            shouldShowDisplayForOrderAheadToggle:
              remoteConfig.orderAheadConfig.shouldShowDisplayForOrderAheadToggle,
          }
        : { enabled: false, shouldShowDisplayForOrderAheadToggle: false },
    externalIdentityVerification: remoteConfig.externalIdentityVerification
      ? {
          enabled: remoteConfig.externalIdentityVerification.enabled,
          provider: remoteConfig.externalIdentityVerification.provider,
        }
      : null,
    ach: {
      enabled: !!remoteConfig.ach?.enabled,
    },
    subBrands:
      remoteConfig.subBrands &&
      remoteConfig.subBrands.enabled &&
      (remoteConfig.subBrands?.list?.length ?? 0) >= MIN_SUB_BRANDS_FOR_SELECTION
        ? { enabled: true, list: remoteConfig.subBrands.list ?? [] }
        : null,
    retailerConnectTobaccoPerformance: {
      enabled: !!remoteConfig.retailerConnectTobaccoPerformance?.enabled,
    },
    retailerConnectMobilePayMetrics: {
      enabled: !!remoteConfig.retailerConnectMobilePayMetrics?.enabled,
      defaultFilter:
        remoteConfig.retailerConnectMobilePayMetrics?.defaultFilter ??
        Location_Metrics_Mobile_Pay_Filter.All,
    },
    retailerConnectRedesignV2: {
      enabled: !!remoteConfig.retailerConnectRedesignV2?.enabled,
    },
    retailerConnectInStoreMetrics: {
      enabled: !!remoteConfig.retailerConnectInStoreMetrics?.enabled,
    },
    triggeredMessages: {
      enabled: !!remoteConfig.triggeredMessages?.enabled,
    },
    externalTobaccoApiOffer: {
      enabled: !!remoteConfig.externalLoyalty?.externalTobaccoApiOffer?.enabled,
    },
    storedValue: {
      enabled: remoteConfig.storedValue?.enabled ?? envConfig.storedValue.enabled ?? false,
    },
    driveInsights: {
      enabled: remoteConfig.driveInsights?.enabled ?? false,
      config: buildDriveInsightsConfig(remoteConfig.driveInsights?.config),
    },
    offerEditor: {
      categoryFieldVisible: !!remoteConfig.offerEditor?.categoryFieldVisible,
    },
  };
};
